import { useEffect, useRef, useState } from 'react';
import { WYSIWYG } from 'src/components/atomsLegacy';
import { SlideOut } from 'src/components/molecules/SlideOut/SlideOut';

interface Props {
  isOpen: boolean;
  closeSummary: () => void;
  saveSummary: (description: string) => void;
  descriptionLastUpdated?: string;
  description?: string;
  hasCanEditPermissions: boolean;
}

export const CaseSummary = ({
  isOpen,
  closeSummary,
  saveSummary,
  descriptionLastUpdated,
  description,
  hasCanEditPermissions,
  ...props
}: Props) => {
  const [newDescriptionHTML, setNewDescriptionHTML] = useState<string>();

  const [newDescriptionMarkdown, setNewDescriptionMarkdown] =
    useState<string>();

  const editorBaselineRef = useRef<{ html: string } | null>(null);

  useEffect(() => {
    editorBaselineRef.current = null;
  }, [description]);

  const isNewDescriptionEmpty = newDescriptionMarkdown
    ? ['', '\u200b'].includes(newDescriptionMarkdown.trim())
    : undefined;

  const contentWasDeleted = isNewDescriptionEmpty && description;

  const isDescriptionEdited =
    editorBaselineRef.current !== null &&
    ((newDescriptionHTML !== undefined &&
      newDescriptionHTML !== editorBaselineRef.current.html &&
      !isNewDescriptionEmpty) ||
      contentWasDeleted);

  const bodyStyle = {
    margin: `${hasCanEditPermissions ? '10px 0 0 0' : '0'}`,
    width: 'auto',
  };

  return (
    <>
      <SlideOut
        isOpen={isOpen}
        title={'Additional case information'}
        onClose={closeSummary}
        onConfirm={() => saveSummary(newDescriptionHTML ?? '')}
        applyButtonDisabled={!isDescriptionEdited}
        confirmButtonText={'Save changes'}
        confirmButtonDataTestid={'save-summary-changes'}
        containerDataTestid={'case-summary'}
        subtitle={
          <>
            Updated: <span>{descriptionLastUpdated}</span>
          </>
        }
        showFooter={hasCanEditPermissions}
        dividerHasPadding={true}
        bodyStyle={bodyStyle}
        className={`${
          hasCanEditPermissions
            ? 'SlideOut--caseSummary--edit'
            : 'SlideOut--caseSummary--view'
        }`}
        {...props}
      >
        <WYSIWYG
          onChange={({ html, markdown }) => {
            setNewDescriptionHTML(html);
            setNewDescriptionMarkdown(markdown);
            if (editorBaselineRef.current === null) {
              editorBaselineRef.current = { html };
            }
          }}
          value={description ?? ''}
          placeholder={'Add information about the case...'}
          reducedMargin
          formatType={'html'}
          readOnly={!hasCanEditPermissions}
        />
      </SlideOut>
    </>
  );
};
